import {
  groupCoursesByTerm,
  computeSummary,
  TranscriptCourseEnrollment,
  TranscriptTermGPA,
  TranscriptConductCase,
} from '../utils';
import TermTable from './TermTable';
import CumulativeTable from './CumulativeTable';
import OfficialTranscriptInfoPage from './OfficialTranscriptInfoPage';
import TranscriptHeader from './TranscriptHeader';

export type TranscriptMode = 'academic' | 'official';

export interface TranscriptProgramEnrollment {
  status?: string | null;
  graduatedAt?: unknown;
  program: {
    name: string;
  };
}

export interface TranscriptUser {
  sandId?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  primaryEmail: string;
  countryOfOrigin?: string | null;
  birthDate?: unknown;
}

interface Props {
  user: TranscriptUser;
  programEnrollments: TranscriptProgramEnrollment[];
  courseEnrollments: TranscriptCourseEnrollment[];
  creditsByCourseDef: Map<string, number>;
  cumulativeGPA: number | null;
  termGPAs: TranscriptTermGPA[];
  conductCases?: TranscriptConductCase[];
  mode?: TranscriptMode;
}

const formatGPA = (gpa: number | null | undefined): string => {
  if (gpa == null) return '—';
  return gpa.toFixed(2);
};

const TranscriptView = ({
  user,
  programEnrollments,
  courseEnrollments,
  creditsByCourseDef,
  cumulativeGPA,
  termGPAs,
  conductCases,
  mode = 'academic',
}: Props) => {
  const isOfficial = mode === 'official';
  const headingText = isOfficial ? 'Official Transcript' : 'Academic Record';
  const dateLabel = isOfficial ? 'Granted' : 'Published';
  const terms = groupCoursesByTerm(
    courseEnrollments,
    creditsByCourseDef,
    termGPAs,
    conductCases
  );
  const summary = computeSummary(terms, cumulativeGPA);
  const semesterGPAs = terms.map((term) => formatGPA(term.gpa));
  const cumulativeGPAs = terms.map((term) => formatGPA(term.cumulativeGPA));

  return (
    <div className="bg-background rounded-xl px-4 md:px-8 pt-5 md:pt-7 pb-6 md:pb-8 flex flex-col gap-6 md:gap-8 items-center shadow-md print:px-0 print:shadow-none">
      <TranscriptHeader
        user={user}
        programEnrollments={programEnrollments}
        headingText={headingText}
        dateLabel={dateLabel}
      />

      {terms.map((term, i) => (
        <TermTable
          key={term.term}
          term={term}
          semesterGPA={semesterGPAs[i]}
          cumulativeGPA={cumulativeGPAs[i]}
        />
      ))}

      <CumulativeTable summary={summary} />

      {isOfficial && <OfficialTranscriptInfoPage />}
    </div>
  );
};

export default TranscriptView;
